import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@helios/ui/button';
import { Modal } from '@helios/ui/overlay';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qAppConfig,
  qAvailableBatchesForDispense,
  qGlobalPharmacyDispenseHistory,
  qPharmacyQueue,
  type AppConfigRow,
  type PatientPharmacyDispenseHistoryRow,
  type PharmacyBatchOptionRow,
  type PharmacyQueueRow,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { useBrandTokens, type ShellOutletContext } from '../../shell/shared';
import { PrescriptionPrintMount, type PrescriptionPrintRequest } from './PrescriptionPrintMount';
import { PharmacyLabelPrintMount, type PharmacyLabelData } from './PharmacyLabelPrint';
import {
  InventoryListToolbar,
  InventoryPageShell,
  InventoryRow,
  InventoryStatusBadge,
  InventoryTable,
} from './_shared';

// ─── Local state ───

interface QueueLineState {
  /** Selected batch for this line (mandatory before Confirm). */
  batchId: string | null;
  batchNumber: string | null;
  batchExpiry: string | null;
  batchAvailable: number | null;
  /** Outcome message after confirm (success/error). */
  message: { kind: 'ok' | 'error'; text: string } | null;
  /** Disable double-click while a confirm is in flight. */
  saving: boolean;
}

const COLUMNS = [
  { label: 'Date', width: '110px' },
  { label: 'Patient', width: '1.2fr' },
  { label: 'Invoice', width: '140px' },
  { label: 'Medicine', width: '1.4fr' },
  { label: 'Batch', width: '180px' },
  { label: 'Qty', width: '110px', align: 'right' as const },
  { label: 'Status', width: '120px' },
  { label: 'Actions', width: '160px', align: 'right' as const },
];

export function PharmacyPage() {
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {
    collapsed: false,
  };
  const brandTokens = useBrandTokens();
  const navigate = useNavigate();
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const userName = user?.displayName ?? user?.email ?? '';

  const { data: queueRows = [] } = usePowerSyncQuery<PharmacyQueueRow>(qPharmacyQueue());
  // PRD.204 — global dispense history feed (read-only).
  const { data: historyRows = [] } = usePowerSyncQuery<PatientPharmacyDispenseHistoryRow>(
    qGlobalPharmacyDispenseHistory(),
  );
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const clinicName = appConfigRows[0]?.org_name ?? null;

  // PRD.204 — view-mode toggle: Ready queue (default) or read-only history.
  const [viewMode, setViewMode] = useState<'queue' | 'history'>('queue');
  const [historyDateFilter, setHistoryDateFilter] = useState<'all' | 'today' | '7d' | '30d'>('all');

  const [search, setSearch] = useState('');
  const [batchModalLineId, setBatchModalLineId] = useState<string | null>(null);
  // PRD.202 — Print Rx state. Triggers a hidden encounter prescription
  // print without leaving the global Pharmacy queue.
  const [rxPrintRequest, setRxPrintRequest] = useState<PrescriptionPrintRequest | null>(null);
  // PRD.202/204 — Print Label state for history rows.
  const [labelToPrint, setLabelToPrint] = useState<PharmacyLabelData | null>(null);

  // Per-invoice-item local state. Reset when the invoice item disappears
  // from the queue (e.g. after confirm — duplicate filter excludes it).
  const [lineState, setLineState] = useState<Record<string, QueueLineState>>({});
  useEffect(() => {
    const validIds = new Set(queueRows.map((r) => r.invoice_item_id));
    setLineState((prev) => {
      const next: Record<string, QueueLineState> = {};
      for (const [k, v] of Object.entries(prev)) if (validIds.has(k)) next[k] = v;
      return next;
    });
  }, [queueRows]);

  function patchLine(invoiceItemId: string, patch: Partial<QueueLineState>) {
    setLineState((prev) => {
      const base: QueueLineState = prev[invoiceItemId] ?? {
        batchId: null,
        batchNumber: null,
        batchExpiry: null,
        batchAvailable: null,
        message: null,
        saving: false,
      };
      return { ...prev, [invoiceItemId]: { ...base, ...patch } };
    });
  }

  const filtered = useMemo(() => {
    if (!search.trim()) return queueRows;
    const q = search.toLowerCase();
    return queueRows.filter((row) => {
      return (
        (row.patient_name ?? '').toLowerCase().includes(q) ||
        (row.invoice_no ?? '').toLowerCase().includes(q) ||
        (row.item_name ?? '').toLowerCase().includes(q) ||
        (row.sku ?? '').toLowerCase().includes(q)
      );
    });
  }, [queueRows, search]);

  // PRD.204 — history filtering. Search matches patient/medicine/SKU/
  // dispense number/invoice/batch; date filter restricts by dispensed_at.
  const filteredHistory = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = Date.now();
    const dayMs = 86400000;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfTodayMs = startOfToday.getTime();
    return historyRows.filter((row) => {
      // Date filter
      if (historyDateFilter !== 'all') {
        const t = row.dispensed_at ? new Date(row.dispensed_at).getTime() : 0;
        if (Number.isNaN(t) || t === 0) return false;
        if (historyDateFilter === 'today' && t < startOfTodayMs) return false;
        if (historyDateFilter === '7d' && now - t > 7 * dayMs) return false;
        if (historyDateFilter === '30d' && now - t > 30 * dayMs) return false;
      }
      if (!q) return true;
      return (
        (row.patient_name ?? '').toLowerCase().includes(q) ||
        (row.item_name_snapshot ?? '').toLowerCase().includes(q) ||
        (row.sku_snapshot ?? '').toLowerCase().includes(q) ||
        (row.dispense_number ?? '').toLowerCase().includes(q) ||
        (row.invoice_no ?? '').toLowerCase().includes(q) ||
        (row.batch_number_snapshot ?? '').toLowerCase().includes(q)
      );
    });
  }, [historyRows, search, historyDateFilter]);

  async function confirmDispense(row: PharmacyQueueRow) {
    if (!db) {
      patchLine(row.invoice_item_id, {
        message: { kind: 'error', text: 'Local database not ready.' },
      });
      return;
    }
    if (!orgId) {
      patchLine(row.invoice_item_id, {
        message: { kind: 'error', text: 'Missing organization context.' },
      });
      return;
    }
    const state = lineState[row.invoice_item_id];
    if (!state?.batchId) {
      patchLine(row.invoice_item_id, {
        message: { kind: 'error', text: 'Select a batch before confirming.' },
      });
      return;
    }
    const qty = row.quantity_remaining ?? 0;
    if (qty <= 0) {
      patchLine(row.invoice_item_id, {
        message: { kind: 'error', text: 'No remaining quantity to dispense.' },
      });
      return;
    }
    if (state.batchAvailable != null && qty > state.batchAvailable) {
      patchLine(row.invoice_item_id, {
        message: {
          kind: 'error',
          text: `Selected batch only has ${state.batchAvailable} available.`,
        },
      });
      return;
    }

    patchLine(row.invoice_item_id, { saving: true, message: null });
    try {
      const dispenseId = crypto.randomUUID();
      const lineId = crypto.randomUUID();
      const now = new Date().toISOString();
      const dispenseNumber = `DISP-${now.slice(0, 10)}-${dispenseId.slice(0, 4)}`;
      const unit = row.inventory_unit ?? row.unit_snapshot ?? null;
      const itemName = row.item_name ?? row.description_snapshot ?? '';

      // 1) Header
      await db.execute(
        `INSERT INTO pharmacy_dispenses (
          id, org_id, dispense_number, patient_id, encounter_id, invoice_id,
          prescription_id, status, dispensed_at, dispensed_by, remarks,
          created_at, updated_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          dispenseId,
          orgId,
          dispenseNumber,
          row.patient_id,
          row.encounter_id,
          row.invoice_id,
          null, // prescription_id — v1 doesn't link prescriptions yet
          'confirmed',
          now,
          userName || userId || null,
          null,
          now,
          now,
        ],
      );

      // 2) Line
      await db.execute(
        `INSERT INTO pharmacy_dispense_lines (
          id, org_id, pharmacy_dispense_id, invoice_item_id, prescription_line_id,
          inventory_item_id, inventory_batch_id,
          item_name_snapshot, sku_snapshot, batch_number_snapshot, expiry_date_snapshot,
          unit_snapshot, instructions_snapshot,
          quantity_to_dispense, quantity_dispensed,
          status, remarks, created_at, updated_at
        ) VALUES (
          ?, ?, ?, ?, ?,
          ?, ?,
          ?, ?, ?, ?,
          ?, ?,
          ?, ?,
          ?, ?, ?, ?
        )`,
        [
          lineId,
          orgId,
          dispenseId,
          row.invoice_item_id,
          null, // prescription_line_id — not wired in v1
          row.inventory_item_id,
          state.batchId,
          itemName,
          row.sku,
          state.batchNumber,
          state.batchExpiry,
          unit,
          null, // instructions_snapshot — placeholder for future prescription join
          qty,
          qty,
          'confirmed',
          null,
          now,
          now,
        ],
      );

      // 3) Outbound stock movement — the actual stock-out event.
      await db.execute(
        `INSERT INTO stock_movements (
          id, org_id, inventory_item_id, inventory_batch_id,
          source_type, source_id, source_line_id,
          movement_type, quantity_in, quantity_out, unit,
          movement_date, remarks, created_by, created_at, updated_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          crypto.randomUUID(),
          orgId,
          row.inventory_item_id,
          state.batchId,
          'pharmacy_dispense',
          dispenseId,
          lineId,
          'pharmacy_dispense',
          0,
          qty,
          unit,
          now.slice(0, 10),
          null,
          userId || null,
          now,
          now,
        ],
      );

      patchLine(row.invoice_item_id, {
        saving: false,
        message: { kind: 'ok', text: 'Dispensed.' },
      });
      // Row will disappear on next query refresh because it's now fully
      // dispensed (excluded by the queue WHERE clause).
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Confirm failed. Please try again.';
      patchLine(row.invoice_item_id, {
        saving: false,
        message: { kind: 'error', text: msg },
      });
    }
  }

  return (
    <InventoryPageShell
      title="Pharmacy"
      subtitle="Dispense Medicine invoice lines that are ready for pharmacy. Confirming a dispense is the only stock-out event."
      collapsed={collapsed}
      onToggleSidebar={onToggleSidebar}
      primaryAction={
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          Medicine invoice lines appear here once the invoice is cleared for dispense.
        </span>
      }
    >
      {/* PRD.204 — Ready/History tab switcher */}
      <PharmacyViewTabs
        viewMode={viewMode}
        onChange={(mode) => {
          setViewMode(mode);
          setSearch('');
        }}
        readyCount={queueRows.length}
        historyCount={historyRows.length}
      />

      <InventoryListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={
          viewMode === 'queue'
            ? 'Search patient, invoice, medicine, SKU...'
            : 'Search patient, medicine, SKU, dispense no, invoice, batch...'
        }
        filters={
          viewMode === 'history' ? (
            <select
              value={historyDateFilter}
              onChange={(e) =>
                setHistoryDateFilter(e.target.value as 'all' | 'today' | '7d' | '30d')
              }
              style={{
                height: 'var(--input-height)',
                padding: '0 var(--input-padding-x)',
                borderRadius: 'var(--input-radius)',
                border: '1px solid var(--border-input)',
                background: 'var(--input-bg)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--input-text)',
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="all">All dates</option>
              <option value="today">Today</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
          ) : undefined
        }
      />
      <div style={{ height: 'var(--sp-4)', flexShrink: 0 }} />
      {viewMode === 'queue' && (
        <InventoryTable
          columns={COLUMNS}
          empty={
            filtered.length === 0
              ? {
                  show: true,
                  title: 'No medicines ready to dispense.',
                  hint:
                    queueRows.length === 0
                      ? 'Medicine invoice lines appear here once they are paid or cleared for Pharmacy.'
                      : 'Try a different search.',
                }
              : undefined
          }
        >
          {filtered.map((row) => {
            const state = lineState[row.invoice_item_id];
            const batchSelected = !!state?.batchId;
            const overAvailable =
              state?.batchAvailable != null && (row.quantity_remaining ?? 0) > state.batchAvailable;
            return (
              <InventoryRow key={row.invoice_item_id} columns={COLUMNS}>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                  {row.invoice_date ?? '—'}
                </span>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
                  {row.patient_name ?? '—'}
                </span>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                  }}
                >
                  {row.invoice_no ?? row.invoice_id?.slice(0, 8) ?? '—'}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--text)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.item_name ?? row.description_snapshot ?? '—'}
                  </div>
                  {row.sku && (
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                      SKU: {row.sku}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setBatchModalLineId(row.invoice_item_id)}
                  style={{
                    height: 32,
                    padding: '0 8px',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)',
                    background: batchSelected ? 'var(--color-success-bg)' : 'var(--bg-surface)',
                    color: batchSelected ? 'var(--color-success-text)' : 'var(--text-muted)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 500,
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                  {batchSelected
                    ? `${state.batchNumber}${
                        state?.batchAvailable != null ? ` · ${state.batchAvailable} avail` : ''
                      }`
                    : 'Select batch…'}
                </button>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: overAvailable ? '#991b1b' : 'var(--text)',
                    textAlign: 'right',
                  }}
                >
                  {row.quantity_remaining ?? 0} {row.inventory_unit ?? row.unit_snapshot ?? ''}
                </span>
                <InventoryStatusBadge
                  status={(row.qty_dispensed ?? 0) > 0 ? 'partial' : 'draft'}
                  label={
                    (row.qty_dispensed ?? 0) > 0
                      ? `Partial (${row.qty_dispensed}/${row.qty_ordered ?? 0})`
                      : 'Ready'
                  }
                />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: 4,
                  }}
                >
                  <Button
                    variant="gradient"
                    size="md"
                    onClick={() => confirmDispense(row)}
                    disabled={!batchSelected || state?.saving === true || overAvailable}
                  >
                    {state?.saving ? 'Saving...' : 'Confirm Dispense'}
                  </Button>
                  {/* PRD.202 — open the encounter Prescriptions tab where the
                    existing Print Prescription action lives. Hidden when
                    no encounter context is on the queue row. */}
                  {row.encounter_id && row.patient_id && (
                    <button
                      type="button"
                      onClick={() =>
                        setRxPrintRequest({
                          patientId: row.patient_id!,
                          encounterId: row.encounter_id!,
                        })
                      }
                      title="Print prescription using the encounter print layout"
                      style={{
                        height: 24,
                        padding: '0 8px',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border)',
                        background: 'var(--bg-surface)',
                        color: 'var(--text-muted)',
                        fontSize: 10,
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      Print Rx
                    </button>
                  )}
                  {state?.message && (
                    <span
                      style={{
                        fontSize: 10,
                        color:
                          state.message.kind === 'ok' ? 'var(--color-success-text)' : '#991b1b',
                      }}
                    >
                      {state.message.text}
                    </span>
                  )}
                </div>
              </InventoryRow>
            );
          })}
        </InventoryTable>
      )}

      {/* PRD.204 — Dispense History (read-only across all patients) */}
      {viewMode === 'history' && (
        <PharmacyHistoryTable
          rows={filteredHistory}
          totalRowCount={historyRows.length}
          onPrintLabel={(row) =>
            setLabelToPrint({
              patientName: row.patient_name ?? '',
              medicineName: row.item_name_snapshot ?? '',
              sku: row.sku_snapshot,
              instructions: row.instructions_snapshot,
              qty: row.quantity_dispensed,
              unit: row.unit_snapshot,
              batchNumber: row.batch_number_snapshot,
              expiryDate: row.expiry_date_snapshot,
              dispensedAt: row.dispensed_at,
              dispenseNumber: row.dispense_number,
              clinicName,
            })
          }
          onPrintRx={(row) => {
            if (row.patient_id && row.encounter_id) {
              setRxPrintRequest({
                patientId: row.patient_id,
                encounterId: row.encounter_id,
              });
            }
          }}
          onOpenPatient={(row) => {
            if (row.patient_id) navigate(`/patient/${row.patient_id}/pharmacy`);
          }}
        />
      )}

      {batchModalLineId && (
        <BatchPickerModal
          itemId={
            queueRows.find((r) => r.invoice_item_id === batchModalLineId)?.inventory_item_id ?? null
          }
          requiredQty={
            queueRows.find((r) => r.invoice_item_id === batchModalLineId)?.quantity_remaining ?? 0
          }
          portalStyle={brandTokens as CSSProperties}
          onClose={() => setBatchModalLineId(null)}
          onSelect={(batch) => {
            patchLine(batchModalLineId, {
              batchId: batch.inventory_batch_id,
              batchNumber: batch.batch_number,
              batchExpiry: batch.expiry_date,
              batchAvailable: batch.available_quantity,
              message: null,
            });
            setBatchModalLineId(null);
          }}
        />
      )}
      <PrescriptionPrintMount
        request={rxPrintRequest}
        onAfterPrint={() => setRxPrintRequest(null)}
      />
      <PharmacyLabelPrintMount data={labelToPrint} onAfterPrint={() => setLabelToPrint(null)} />
    </InventoryPageShell>
  );
}

// ─── PRD.204 — view-mode tabs ───

function PharmacyViewTabs({
  viewMode,
  onChange,
  readyCount,
  historyCount,
}: {
  viewMode: 'queue' | 'history';
  onChange: (mode: 'queue' | 'history') => void;
  readyCount: number;
  historyCount: number;
}) {
  const TABS: { value: 'queue' | 'history'; label: string; count: number }[] = [
    { value: 'queue', label: 'Ready to Dispense', count: readyCount },
    { value: 'history', label: 'Dispense History', count: historyCount },
  ];
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3) var(--sp-6) 0',
        borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}
    >
      {TABS.map((t) => {
        const active = viewMode === t.value;
        return (
          <button
            key={t.value}
            type="button"
            onClick={() => onChange(t.value)}
            style={{
              padding: 'var(--sp-2) var(--sp-3)',
              border: 'none',
              borderBottom: active ? '2px solid var(--color-primary)' : '2px solid transparent',
              background: 'transparent',
              color: active ? 'var(--text)' : 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: active ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: -1,
            }}
          >
            {t.label}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 22,
                height: 18,
                padding: '0 6px',
                borderRadius: 'var(--radius-full)',
                background: active ? 'var(--color-primary)' : 'var(--bg-surface-2)',
                color: active ? '#fff' : 'var(--text-muted)',
                fontSize: 10,
                fontWeight: 700,
              }}
            >
              {t.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── PRD.204 — Dispense History table ───

function PharmacyHistoryTable({
  rows,
  totalRowCount,
  onPrintLabel,
  onPrintRx,
  onOpenPatient,
}: {
  rows: PatientPharmacyDispenseHistoryRow[];
  totalRowCount: number;
  onPrintLabel: (row: PatientPharmacyDispenseHistoryRow) => void;
  onPrintRx: (row: PatientPharmacyDispenseHistoryRow) => void;
  onOpenPatient: (row: PatientPharmacyDispenseHistoryRow) => void;
}) {
  if (rows.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--sp-8)',
          textAlign: 'center',
          color: 'var(--text-muted)',
        }}
      >
        <div
          style={{
            fontSize: 'var(--font-size-md)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
            marginBottom: 4,
          }}
        >
          {totalRowCount === 0
            ? 'No dispenses recorded yet.'
            : 'No dispense history matches your filters.'}
        </div>
        <div style={{ fontSize: 'var(--font-size-sm)' }}>
          {totalRowCount === 0
            ? 'Confirmed Pharmacy dispenses appear here automatically.'
            : 'Try adjusting your search or date filter.'}
        </div>
      </div>
    );
  }
  return (
    <div
      style={{
        margin: '0 var(--sp-6) var(--sp-6)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <Th>Dispense No.</Th>
            <Th>Date</Th>
            <Th>Patient</Th>
            <Th>Medicine</Th>
            <Th align="right">Qty</Th>
            <Th>Batch</Th>
            <Th>Expiry</Th>
            <Th>Invoice</Th>
            <Th align="right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.pharmacy_dispense_line_id}
              style={{ borderTop: '1px solid var(--border)' }}
            >
              <Td>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                  }}
                >
                  {row.dispense_number ?? row.pharmacy_dispense_id?.slice(0, 8) ?? '—'}
                </span>
              </Td>
              <Td>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                  {row.dispensed_at ? row.dispensed_at.replace('T', ' ').slice(0, 16) : '—'}
                </span>
              </Td>
              <Td>
                <button
                  type="button"
                  onClick={() => onOpenPatient(row)}
                  disabled={!row.patient_id}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: row.patient_id ? 'var(--color-primary)' : 'var(--text)',
                    cursor: row.patient_id ? 'pointer' : 'default',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    textAlign: 'left',
                  }}
                  title={row.patient_id ? 'Open patient pharmacy' : undefined}
                >
                  {row.patient_name ?? '—'}
                </button>
              </Td>
              <Td>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text)',
                  }}
                >
                  {row.item_name_snapshot ?? '—'}
                </div>
                {row.sku_snapshot && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    SKU: {row.sku_snapshot}
                  </div>
                )}
              </Td>
              <Td align="right">
                {row.quantity_dispensed ?? 0} {row.unit_snapshot ?? ''}
              </Td>
              <Td>{row.batch_number_snapshot ?? '—'}</Td>
              <Td>{row.expiry_date_snapshot ?? '—'}</Td>
              <Td>{row.invoice_no ?? row.invoice_id?.slice(0, 8) ?? '—'}</Td>
              <Td align="right">
                <div style={{ display: 'inline-flex', gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => onPrintRx(row)}
                    disabled={!row.encounter_id || !row.patient_id}
                    title={
                      row.encounter_id && row.patient_id
                        ? 'Print prescription using the encounter print layout'
                        : "Prescription print is available from the encounter's Prescriptions tab."
                    }
                    style={{
                      height: 28,
                      padding: '0 8px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-surface)',
                      color:
                        row.encounter_id && row.patient_id ? 'var(--text)' : 'var(--text-disabled)',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 500,
                      cursor: row.encounter_id && row.patient_id ? 'pointer' : 'not-allowed',
                    }}
                  >
                    Print Rx
                  </button>
                  <button
                    type="button"
                    onClick={() => onPrintLabel(row)}
                    title="Print medicine label using dispense snapshots"
                    style={{
                      height: 28,
                      padding: '0 8px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-surface)',
                      color: 'var(--text)',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    Print Label
                  </button>
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Td({
  children,
  align = 'left',
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <td
      style={{
        padding: '10px 12px',
        textAlign: align,
        fontSize: 'var(--font-size-sm)',
        color: 'var(--text)',
        verticalAlign: 'top',
      }}
    >
      {children}
    </td>
  );
}

// ─── Batch picker modal — existing batches only (PRD.194 §12) ───

function BatchPickerModal({
  itemId,
  requiredQty,
  portalStyle,
  onClose,
  onSelect,
}: {
  itemId: string | null;
  requiredQty: number;
  portalStyle: CSSProperties;
  onClose: () => void;
  onSelect: (batch: PharmacyBatchOptionRow) => void;
}) {
  const { data: batchRows = [] } = usePowerSyncQuery<PharmacyBatchOptionRow>(
    itemId
      ? qAvailableBatchesForDispense(itemId)
      : // Always-empty placeholder query when no item is selected
        {
          key: ['pharmacy-available-batches', '__none__'] as const,
          sql: `SELECT NULL AS inventory_batch_id, NULL AS batch_number, NULL AS expiry_date, 0 AS available_quantity WHERE 1=0`,
          params: [],
          watch: ['inventory_batches', 'stock_movements'],
        },
  );

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      title="Select Batch"
      maxWidth={560}
      portalStyle={portalStyle}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outline" size="md" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div style={{ padding: 'var(--sp-3) var(--sp-5) var(--sp-5)' }}>
        <p
          style={{
            margin: '0 0 var(--sp-3)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
          }}
        >
          Pick an existing batch with at least <strong>{requiredQty}</strong> available. New batches
          are created via Delivery Order receiving or Inventory Adjustments — not from Pharmacy.
        </p>
        {batchRows.length === 0 ? (
          <div
            style={{
              padding: 'var(--sp-6)',
              textAlign: 'center',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            No available batches for this medicine. Receive stock or create an inventory adjustment
            first.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <Th>Batch Number</Th>
                <Th>Expiry</Th>
                <Th align="right">Available</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {batchRows.map((b) => {
                const avail = b.available_quantity ?? 0;
                const insufficient = avail < requiredQty;
                return (
                  <tr
                    key={b.inventory_batch_id}
                    style={{ borderBottom: '1px solid var(--border)' }}
                  >
                    <td style={{ padding: '8px 6px', fontSize: 'var(--font-size-sm)' }}>
                      {b.batch_number ?? '—'}
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {b.expiry_date ?? '—'}
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        textAlign: 'right',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: insufficient ? '#991b1b' : 'var(--text)',
                      }}
                    >
                      {avail}
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                      <Button
                        variant="outline"
                        size="md"
                        disabled={avail <= 0}
                        onClick={() => onSelect(b)}
                      >
                        Select
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      style={{
        padding: '10px 8px',
        textAlign: align,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface-2, #fafafa)',
      }}
    >
      {children}
    </th>
  );
}
